import React, { useEffect, useState } from 'react';
import {
  PageHeader, Btn, Badge, Card, MetricCard, SearchBox, SegmentedControl, Field, Input, TextArea,
  Toggle, BmModal, ConfirmModal, EmptyState, Loading, ErrorState, Select, Tile, Overline,
} from '../ForBrands/ui/BrandUI';
import { Icon } from '../ForBrands/ui/icons';
import '../ForBrands/brandMode.css';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { toast } from 'sonner';
import { API_BASE_URL, BASE_URL } from '../../constants';
import PartnerApiDocs from '../common/PartnerApiDocs';

const API_KEYS_URL = `${API_BASE_URL}/admin/api-keys/`;

// Docs always show the production base so admins can hand snippets straight
// to partners; the local base is used for the quick-test curl after creating
// a key in dev.
const PROD_BASE = 'https://app.cachescore.com';
const ENV_BASE = BASE_URL || PROD_BASE;

// The V2 positive colour (bm-positive) for the Active badge dot.
const POSITIVE_DOT = '#1f9d55';

const initialForm = {
  name: '',
  contactEmail: '',
  rateLimitPerMin: 120,
  allowedOrigins: '',
  notes: '',
  active: true,
};

const fmtDate = (iso) => (iso ? iso.slice(0, 16).replace('T', ' ') : 'Never');

const copyText = (text, label) => {
  navigator.clipboard.writeText(text);
  toast.success(`${label} copied to clipboard.`);
};

// Revoked wins over the active flag: a revoked key never serves again.
export const KeyStatusBadge = ({ apiKey }) => {
  if (apiKey.revoked) return <Badge tone="critical">Revoked</Badge>;
  if (apiKey.active) return <Badge tone="outline" dot={POSITIVE_DOT}>Active</Badge>;
  return <Badge tone="passive">Disabled</Badge>;
};

const TABLE_HEADERS = ['Name', 'Key', 'Status', 'Plan', 'Rate limit', 'Requests', 'Last used', 'Created'];
const TH = 'whitespace-nowrap border-b border-bm-border px-4 py-2.5 text-left bm-label-xs uppercase tracking-[0.06em] text-bm-muted';
const TD = 'px-4 py-3 bm-body-m text-bm-ink';
const MONO_BOX = 'min-w-0 flex-1 break-all rounded-[8px] border border-bm-border bg-bm-subtle px-3 py-2.5 font-mono text-xs';

/* --------------------------- plans and credits --------------------------- */

// Plans are sold on keys (api_plans.py on the server, the one place the
// prices, allowances and packages live; the list and plan endpoints send
// them as `plans`). These labels only cover a server that sent none.
const PLAN_LABELS = {
  full: 'Full access',
  performance: 'Performance',
  performance_visibility: 'Performance + Visibility',
};

const fmtNum = (n) => Number(n || 0).toLocaleString('en-US');
const fmtUsd = (n) => `$${fmtNum(n)}`;
const fmtDay = (iso) => (iso ? iso.slice(0, 10) : '');
const fmtSigned = (n) => `${n > 0 ? '+' : ''}${fmtNum(n)}`;

const planLabelOf = (k) => k.planLabel || PLAN_LABELS[k.plan] || PLAN_LABELS.full;

// An active plan whose period ended (past the server's grace days): its
// monthly credits are paused until a new period starts.
const periodEnded = (k) => Boolean(k.metered) && k.planStatus !== 'lapsed' && k.monthlyUsable === false;

// "Performance, $200 a month, 1,000 credits"; full access is by agreement.
export const planOptionLabel = (p) => (
  p.metered
    ? `${p.label}, ${fmtUsd(p.priceMonthlyUsd)} a month, ${fmtNum(p.monthlyCredits)} credits`
    : `${p.label} (by agreement, no credits)`
);

// The table's Plan cell: the plan, and for a plan key its credits.
export const PlanCell = ({ apiKey }) => {
  if (!apiKey.metered) {
    return (
      <>
        <div className="whitespace-nowrap bm-label-l text-bm-ink">{planLabelOf(apiKey)}</div>
        <div className="whitespace-nowrap bm-body-s text-bm-muted">No credit limit</div>
      </>
    );
  }
  return (
    <>
      <div className="flex items-center gap-2 whitespace-nowrap">
        <span className="bm-label-l text-bm-ink">{planLabelOf(apiKey)}</span>
        {apiKey.planStatus === 'lapsed' && <Badge tone="attention">Lapsed</Badge>}
        {periodEnded(apiKey) && <Badge tone="attention">Period ended</Badge>}
      </div>
      <div className="whitespace-nowrap bm-body-s text-bm-muted">
        {fmtNum(apiKey.monthlyCredits)} of {fmtNum(apiKey.monthlyAllowance)} monthly · {fmtNum(apiKey.purchasedCredits)} purchased
      </div>
    </>
  );
};

const LEDGER_KINDS = {
  grant: 'Granted',
  purchase: 'Purchased',
  monthly: 'Monthly allowance',
  debit: 'Used',
  adjust: 'Adjusted',
};

const LEDGER_BY = { stripe: 'Stripe', system: 'Automatic', api: 'API' };

const CREDIT_CHANGES = [
  { value: 'grant', label: 'Grant purchased credits' },
  { value: 'adjust_purchased', label: 'Adjust purchased credits' },
  { value: 'adjust_monthly', label: 'Adjust monthly credits' },
];

const planFormOf = (k) => ({
  plan: k.plan || 'full',
  planStatus: k.planStatus || 'active',
  periodStart: fmtDay(k.periodStart),
  periodEnd: fmtDay(k.periodEnd),
});

// One key's plan, credits, payment links and ledger (Admin > API keys >
// Plan). Every change goes to the plan endpoint; payment links are Stripe
// Checkout links the admin copies to the partner (nothing is emailed).
export const PlanCreditsModal = ({ apiKey, plans, authHeaders, onClose, onChanged }) => {
  const planUrl = `${API_KEYS_URL}${apiKey.id}/plan/`;
  const linkUrl = `${API_KEYS_URL}${apiKey.id}/payment-link/`;

  const [detail, setDetail] = useState(null);
  const [catalog, setCatalog] = useState(plans || null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [planForm, setPlanForm] = useState(planFormOf(apiKey));
  const [credit, setCredit] = useState({ change: 'grant', amount: '', note: '' });
  // The action in flight ('set', 'grant', 'link', ...), '' when idle.
  const [busy, setBusy] = useState('');
  const [link, setLink] = useState(null);

  const current = detail?.apiKey || apiKey;
  const metered = Boolean(current.metered);
  const planChoices = catalog?.plans?.length
    ? catalog.plans
    : Object.keys(PLAN_LABELS).map((id) => ({ id, label: PLAN_LABELS[id], metered: id !== 'full' }));
  const soldPlans = (catalog?.plans || []).filter((p) => p.sold);
  const packages = catalog?.packages || [];
  const ledger = detail?.ledger || [];
  const lastUse = (detail?.recentDebits || [])[0];

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(planUrl, authHeaders);
      setDetail(res.data);
      if (res.data.plans) setCatalog(res.data.plans);
      setPlanForm(planFormOf(res.data.apiKey || apiKey));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load the plan.');
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const post = async (action, body, success) => {
    setBusy(action);
    setError('');
    try {
      const res = await axios.post(planUrl, { action, ...body }, authHeaders);
      setDetail((d) => ({ ...(d || {}), ...res.data }));
      if (res.data.apiKey) {
        setPlanForm(planFormOf(res.data.apiKey));
        onChanged(res.data.apiKey);
      }
      toast.success(success);
      return true;
    } catch (err) {
      setError(err.response?.data?.error || 'That change did not save.');
      return false;
    } finally {
      setBusy('');
    }
  };

  // Dates go out only when the admin changed them: the server starts a
  // fresh monthly allowance when the period start moves.
  const savePlan = (e) => {
    e.preventDefault();
    const before = planFormOf(current);
    const body = { plan: planForm.plan, planStatus: planForm.planStatus };
    if (planForm.periodStart !== before.periodStart) body.periodStart = planForm.periodStart;
    if (planForm.periodEnd !== before.periodEnd) body.periodEnd = planForm.periodEnd;
    post('set', body, 'Plan saved.');
  };

  const applyCredits = async (e) => {
    e.preventDefault();
    const grant = credit.change === 'grant';
    const amount = Number(String(credit.amount).replace(/,/g, '').trim());
    if (!Number.isInteger(amount) || amount === 0 || (grant && amount < 0)) {
      setError(grant
        ? 'Grant a whole number of credits above 0.'
        : 'Adjust by a whole number of credits. Use a minus sign to take credits away.');
      return;
    }
    if (!credit.note.trim()) {
      setError('Add a note saying why.');
      return;
    }
    const body = grant
      ? { amount, note: credit.note.trim() }
      : { amount, bucket: credit.change === 'adjust_monthly' ? 'monthly' : 'purchased', note: credit.note.trim() };
    const saved = await post(grant ? 'grant' : 'adjust', body, grant ? 'Credits granted.' : 'Credits adjusted.');
    if (saved) setCredit({ ...credit, amount: '', note: '' });
  };

  const makeLink = async (body, label) => {
    setBusy('link');
    setError('');
    setLink(null);
    try {
      const res = await axios.post(linkUrl, body, authHeaders);
      setLink({ url: res.data.url, label });
    } catch (err) {
      setError(err.response?.data?.error || 'Could not create the payment link.');
    }
    setBusy('');
  };

  let planState = 'Active';
  if (current.planStatus === 'lapsed') planState = 'Lapsed, purchased credits only';
  else if (periodEnded(current)) planState = 'Period ended, monthly credits paused';
  const statusLine = !metered
    ? 'Every field, no credits'
    : `${planState}${current.billing === 'stripe' ? ' · Billed through Stripe' : ''}`;

  return (
    <BmModal
      title="Plan and credits"
      onClose={() => { if (!busy) onClose(); }}
      footer={<Btn variant="tertiary" className="ml-auto" onClick={onClose} disabled={!!busy}>Done</Btn>}
      width={680}
    >
      <div className="flex flex-col gap-6">
        <div className="bm-body-m text-bm-muted">{apiKey.name}</div>

        {error && <ErrorState role="alert">{error}</ErrorState>}

        {loading ? (
          <Loading label="Loading the plan..." />
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Tile label="Plan" value={planLabelOf(current)} sub={statusLine} />
              <Tile
                label="Monthly credits"
                value={metered ? fmtNum(current.monthlyCredits) : 'None'}
                sub={metered ? `of ${fmtNum(current.monthlyAllowance)}, no rollover` : 'Not metered'}
              />
              <Tile label="Purchased credits" value={fmtNum(current.purchasedCredits)} sub="Never expire" />
              <Tile
                label="Period ends"
                value={metered ? (fmtDay(current.periodEnd) || 'Open') : 'None'}
                sub={metered && current.periodStart ? `Started ${fmtDay(current.periodStart)}` : ''}
              />
            </div>

            <form onSubmit={savePlan} className="flex flex-col gap-4" aria-label="Plan settings">
              <Overline>Plan</Overline>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Plan">
                  <Select
                    aria-label="Plan"
                    value={planForm.plan}
                    onChange={(e) => setPlanForm({ ...planForm, plan: e.target.value })}
                  >
                    {planChoices.map((p) => <option key={p.id} value={p.id}>{planOptionLabel(p)}</option>)}
                  </Select>
                </Field>
                <Field label="Status">
                  <Select
                    aria-label="Status"
                    value={planForm.planStatus}
                    onChange={(e) => setPlanForm({ ...planForm, planStatus: e.target.value })}
                  >
                    <option value="active">Active</option>
                    <option value="lapsed">Lapsed (purchased credits only)</option>
                  </Select>
                </Field>
                <Field label="Period start">
                  <Input
                    type="date"
                    aria-label="Period start"
                    value={planForm.periodStart}
                    onChange={(e) => setPlanForm({ ...planForm, periodStart: e.target.value })}
                  />
                </Field>
                <Field label="Period end" hint="Leave blank for an open period.">
                  <Input
                    type="date"
                    aria-label="Period end"
                    value={planForm.periodEnd}
                    onChange={(e) => setPlanForm({ ...planForm, periodEnd: e.target.value })}
                  />
                </Field>
              </div>
              <p className="bm-body-s text-bm-muted">
                A new plan or a new period start gives the key a fresh monthly allowance. The allowance
                renews every month of the period and unused monthly credits do not roll over.
              </p>
              {current.billing === 'stripe' && (
                <p className="bm-body-s text-bm-muted">
                  This key pays through Stripe. The next paid invoice sets the plan and period again,
                  so change or cancel the subscription in Stripe too.
                </p>
              )}
              <div className="flex flex-wrap gap-2">
                <Btn type="submit" size="sm" disabled={!!busy}>{busy === 'set' ? 'Saving...' : 'Save plan'}</Btn>
                {metered && (
                  <Btn
                    variant="secondary"
                    size="sm"
                    disabled={!!busy}
                    onClick={() => post('new_period', {}, 'New period started.')}
                  >
                    Start a new period
                  </Btn>
                )}
              </div>
            </form>

            <form onSubmit={applyCredits} className="flex flex-col gap-4" aria-label="Grant or adjust credits">
              <Overline>Credits</Overline>
              <div className="grid gap-4 sm:grid-cols-[1fr_160px]">
                <Field label="Change">
                  <Select
                    aria-label="Credit change"
                    value={credit.change}
                    onChange={(e) => setCredit({ ...credit, change: e.target.value })}
                  >
                    {CREDIT_CHANGES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </Select>
                </Field>
                <Field label="Credits">
                  <Input
                    type="number"
                    aria-label="Credits"
                    value={credit.amount}
                    onChange={(e) => setCredit({ ...credit, amount: e.target.value })}
                    placeholder={credit.change === 'grant' ? '1000' : '-100'}
                  />
                </Field>
              </div>
              <Field label="Note">
                <Input
                  aria-label="Note"
                  value={credit.note}
                  onChange={(e) => setCredit({ ...credit, note: e.target.value })}
                  placeholder="Why, for the ledger"
                />
              </Field>
              <div>
                <Btn type="submit" variant="secondary" size="sm" disabled={!!busy}>
                  {busy === 'grant' || busy === 'adjust' ? 'Saving...' : 'Apply'}
                </Btn>
              </div>
            </form>

            <div className="flex flex-col gap-3">
              <Overline>Payment links</Overline>
              <p className="bm-body-s text-bm-muted">
                Stripe Checkout links stay open for 24 hours. Copy one and send it to the partner.
                Nothing is emailed. A paid plan starts its period and monthly credits at once.
              </p>
              <div className="flex flex-wrap gap-2">
                {soldPlans.map((p) => {
                  const label = `${p.label} plan, ${fmtUsd(p.priceMonthlyUsd)} a month`;
                  return (
                    <Btn
                      key={p.id}
                      variant="secondary"
                      size="sm"
                      disabled={!!busy}
                      onClick={() => makeLink({ kind: 'plan', plan: p.id }, label)}
                    >
                      <Icon name="credit-card" size={14} className="mr-1.5" /> {label}
                    </Btn>
                  );
                })}
                {/* A full access key never spends credits, so it is never
                    sold a package (the server refuses one too). */}
                {metered && packages.map((pk) => {
                  const label = `${fmtNum(pk.credits)} credits, ${fmtUsd(pk.priceUsd)}`;
                  return (
                    <Btn
                      key={pk.id}
                      variant="secondary"
                      size="sm"
                      disabled={!!busy}
                      onClick={() => makeLink({ kind: 'credits', package: pk.id }, label)}
                    >
                      {label}
                    </Btn>
                  );
                })}
              </div>
              {!metered && packages.length > 0 && (
                <p className="bm-body-s text-bm-muted">
                  Credit packages are for plan keys. Full access keys use no credits.
                </p>
              )}
              {busy === 'link' && <span className="bm-body-s text-bm-muted">Creating the link...</span>}
              {link && (
                <div className="flex flex-col gap-[6px]">
                  <span className="bm-label-l text-bm-ink">Link for {link.label}</span>
                  <div className="flex items-center gap-2">
                    <code className={`${MONO_BOX} text-bm-ink`}>{link.url}</code>
                    <Btn variant="secondary" size="sm" onClick={() => copyText(link.url, 'Payment link')} aria-label="Copy payment link">
                      <Icon name="copy" size={14} className="mr-1.5" /> Copy
                    </Btn>
                  </div>
                </div>
              )}
            </div>

            <div className="flex flex-col gap-3">
              <Overline>Recent activity</Overline>
              <p className="bm-body-s text-bm-muted">
                {fmtNum(detail?.usedCredits)} credits used in the last {detail?.usedDays || 30} days.
                {lastUse ? ` Last use ${fmtDate(lastUse.at)}.` : ''}
              </p>
              {ledger.length === 0 ? (
                <EmptyState>No plan or credit changes yet.</EmptyState>
              ) : (
                <div className="w-full max-w-full overflow-x-auto rounded-[8px] border border-bm-border">
                  <table className="w-full text-left" aria-label="Credit ledger">
                    <thead className="bg-bm-subtle">
                      <tr>
                        {['When', 'Change', 'Credits', 'By', 'Note'].map((h) => <th key={h} className={TH}>{h}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {ledger.map((e) => (
                        <tr key={e.id} className="border-b border-bm-border last:border-0">
                          <td className={`${TD} whitespace-nowrap text-bm-muted`}>{fmtDate(e.at)}</td>
                          <td className={`${TD} whitespace-nowrap`}>{LEDGER_KINDS[e.kind] || e.kind}</td>
                          <td className={`${TD} whitespace-nowrap`}>{fmtSigned(e.amount)}</td>
                          <td className={`${TD} whitespace-nowrap text-bm-muted`}>{LEDGER_BY[e.by] || e.by}</td>
                          <td className={`${TD} text-bm-muted`}>{e.note}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </BmModal>
  );
};

/* ------------------------------- keys tab -------------------------------- */

// `plans`: the plan catalog the key list call returns (prices, allowances,
// packages); the page keeps it so the docs tab shows the same plans.
const ApiKeysTab = ({ plans, onPlans }) => {
  const token = useSelector((state) => state.auth.token);

  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [editKey, setEditKey] = useState(null); // null = create mode
  const [form, setForm] = useState(initialForm);
  const [modalError, setModalError] = useState('');
  const [saving, setSaving] = useState(false);

  // Set once right after a successful create; the plaintext key is shown a
  // single time and can never be fetched again.
  const [createdKey, setCreatedKey] = useState(null);

  // The key waiting on the revoke confirmation, and whether the call runs.
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [revoking, setRevoking] = useState(false);

  // The key whose Plan and credits modal is open.
  const [planKey, setPlanKey] = useState(null);

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const fetchKeys = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(API_KEYS_URL, authHeaders);
      setKeys(res.data.apiKeys || []);
      onPlans(res.data.plans || null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load API keys.');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchKeys();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const openCreateModal = () => {
    setEditKey(null);
    setForm(initialForm);
    setCreatedKey(null);
    setModalError('');
    setShowModal(true);
  };

  const openEditModal = (k) => {
    setEditKey(k);
    setForm({
      name: k.name,
      contactEmail: k.contactEmail || '',
      rateLimitPerMin: k.rateLimitPerMin,
      allowedOrigins: (k.allowedOrigins || []).join(', '),
      notes: k.notes || '',
      active: k.active,
    });
    setCreatedKey(null);
    setModalError('');
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditKey(null);
    setForm(initialForm);
    setModalError('');
    if (createdKey) fetchKeys();
    setCreatedKey(null);
  };

  // Cancel, the X, Escape and the backdrop wait for a create or update call
  // in flight: closing mid-create would hide the show-once key for good.
  // Once the key is revealed, only Done or the X close it (the BmModal is
  // not dismissible then), so a stray Escape or click cannot lose it.
  const dismissModal = () => {
    if (!saving) closeModal();
  };

  const buildPayload = () => ({
    name: form.name.trim(),
    contactEmail: form.contactEmail.trim(),
    rateLimitPerMin: Number(form.rateLimitPerMin) || 0,
    allowedOrigins: form.allowedOrigins
      .split(/[\n,]/)
      .map((o) => o.trim())
      .filter(Boolean),
    notes: form.notes.trim(),
  });

  const handleCreate = async (e) => {
    e.preventDefault();
    setModalError('');
    setSaving(true);
    try {
      const res = await axios.post(API_KEYS_URL, buildPayload(), authHeaders);
      setCreatedKey(res.data);
    } catch (err) {
      setModalError(err.response?.data?.error || 'Failed to create the API key.');
    }
    setSaving(false);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setModalError('');
    setSaving(true);
    try {
      await axios.put(
        `${API_KEYS_URL}${editKey.id}/`,
        { ...buildPayload(), active: form.active },
        authHeaders,
      );
      toast.success('API key updated.');
      closeModal();
      fetchKeys();
    } catch (err) {
      setModalError(err.response?.data?.error || 'Failed to update the API key.');
    }
    setSaving(false);
  };

  const handleToggleActive = async (k) => {
    try {
      await axios.put(`${API_KEYS_URL}${k.id}/`, { active: !k.active }, authHeaders);
      toast.success(k.active ? 'Key disabled.' : 'Key enabled.');
      fetchKeys();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update the key.');
    }
  };

  const handleRevoke = async () => {
    const k = revokeTarget;
    if (!k) return;
    setRevoking(true);
    try {
      await axios.delete(`${API_KEYS_URL}${k.id}/`, authHeaders);
      toast.success('API key revoked.');
      fetchKeys();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to revoke the key.');
    }
    setRevoking(false);
    setRevokeTarget(null);
  };

  const filteredKeys = keys.filter((k) =>
    `${k.name} ${k.contactEmail}`
      .toLowerCase()
      .includes(search.toLowerCase()));

  const activeCount = keys.filter((k) => k.active && !k.revoked).length;
  const totalRequests = keys.reduce((sum, k) => sum + (k.requestCount || 0), 0);

  const curlExample = createdKey
    ? `curl -H "X-API-Key: ${createdKey.apiKey}" ${ENV_BASE}/api/partner/v1/athletes/`
    : '';

  const modalTitle = createdKey ? 'API key created' : editKey ? 'Edit API key' : 'New API key';

  const modalFooter = createdKey ? (
    <Btn variant="tertiary" className="ml-auto" onClick={closeModal}>Done</Btn>
  ) : (
    <>
      <Btn variant="secondary" className="ml-auto" onClick={dismissModal} disabled={saving}>Cancel</Btn>
      <Btn type="submit" form="api-key-form" disabled={saving}>
        {saving ? 'Saving...' : editKey ? 'Update' : 'Create key'}
      </Btn>
    </>
  );

  let tableBody;
  if (loading) {
    tableBody = <Loading label="Loading API keys..." />;
  } else if (filteredKeys.length === 0) {
    tableBody = (
      <EmptyState className="m-6">
        {keys.length === 0
          ? 'No API keys yet. Create one to give a partner platform access.'
          : 'No API keys match this search.'}
      </EmptyState>
    );
  } else {
    tableBody = (
      <div className="w-full max-w-full overflow-x-auto">
        <table className="w-full text-left">
          <thead className="bg-bm-subtle">
            <tr>
              {TABLE_HEADERS.map((h) => <th key={h} className={TH}>{h}</th>)}
              <th className={`${TH} !text-right`}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredKeys.map((k) => (
              <tr key={k.id} className="border-b border-bm-border bg-white transition-colors last:border-0 hover:bg-bm-subtle">
                <td className={TD}>
                  <div className="bm-label-l text-bm-ink">{k.name}</div>
                  {k.contactEmail && <div className="bm-body-s text-bm-muted">{k.contactEmail}</div>}
                </td>
                <td className="whitespace-nowrap px-4 py-3 font-mono text-xs text-bm-muted">{k.keyPrefix}...</td>
                <td className={TD}><KeyStatusBadge apiKey={k} /></td>
                <td className={TD}><PlanCell apiKey={k} /></td>
                <td className={`${TD} whitespace-nowrap`}>
                  {k.rateLimitPerMin > 0 ? `${k.rateLimitPerMin}/min` : 'Unlimited'}
                </td>
                <td className={TD}>{k.requestCount}</td>
                <td className={`${TD} whitespace-nowrap text-bm-muted`}>{fmtDate(k.lastUsedAt)}</td>
                <td className={`${TD} whitespace-nowrap text-bm-muted`}>{fmtDate(k.createdAt)}</td>
                <td className="px-4 py-3">
                  {!k.revoked && (
                    <div className="flex items-center justify-end gap-1">
                      <Btn
                        variant="ghost"
                        size="sm"
                        className="w-8 !px-0"
                        onClick={() => openEditModal(k)}
                        aria-label="Edit"
                        title="Edit"
                      >
                        <Icon name="edit" size={15} />
                      </Btn>
                      <Btn
                        variant="ghost"
                        size="sm"
                        onClick={() => setPlanKey(k)}
                        aria-label="Plan and credits"
                        title="Plan and credits"
                      >
                        <Icon name="credit-card" size={15} className="mr-1" /> Plan
                      </Btn>
                      <Btn
                        variant="ghost"
                        size="sm"
                        onClick={() => handleToggleActive(k)}
                        title={k.active ? 'Disable temporarily' : 'Re-enable'}
                      >
                        {k.active ? 'Disable' : 'Enable'}
                      </Btn>
                      <Btn
                        variant="danger"
                        size="sm"
                        onClick={() => setRevokeTarget(k)}
                        aria-label="Revoke"
                        title="Revoke permanently"
                      >
                        <Icon name="x-circle" size={14} className="mr-1" />
                        Revoke
                      </Btn>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Stat tiles */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <MetricCard label="Total keys" value={keys.length} />
        <MetricCard label="Active keys" value={activeCount} />
        <MetricCard label="Total requests" value={totalRequests.toLocaleString()} />
      </div>

      {/* Toolbar */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <SearchBox
          value={search}
          onChange={setSearch}
          placeholder="Search by name or contact"
          aria-label="Search API keys"
          className="w-full sm:max-w-sm"
        />
        <Btn onClick={openCreateModal}>
          <Icon name="plus" size={16} className="mr-1.5" /> New API key
        </Btn>
      </div>

      {error && <ErrorState>{error}</ErrorState>}

      {!(error && keys.length === 0) && (
        <Card padding="p-0" className="w-full min-w-0 max-w-full overflow-hidden">
          {tableBody}
        </Card>
      )}

      {showModal && (
        <BmModal
          title={modalTitle}
          onClose={dismissModal}
          footer={modalFooter}
          width={560}
          dismissible={!createdKey}
        >
          {createdKey ? (
            /* Show-once reveal, same pattern as the invite-admin temp password */
            <div className="flex flex-col gap-5">
              <div className="flex items-center gap-2 bm-label-l text-bm-ink">
                <Icon name="check-circle" size={18} className="text-bm-positive" />
                Key created for {createdKey.name}.
              </div>
              <div className="flex flex-col items-start gap-2 rounded-[8px] border border-bm-border bg-bm-subtle px-4 py-3 bm-body-m text-bm-muted">
                <Badge tone="attention">Shown once</Badge>
                <span>
                  Copy the key now and share it with the partner over a secure channel.
                  For security it is stored hashed, so it cannot be shown again. If it
                  is lost, revoke it and create a new one.
                </span>
              </div>
              <div className="flex flex-col gap-[6px]">
                <span className="bm-label-l text-bm-ink">API key</span>
                <div className="flex items-center gap-2">
                  <code className={`${MONO_BOX} text-bm-ink`}>{createdKey.apiKey}</code>
                  <Btn variant="secondary" size="sm" onClick={() => copyText(createdKey.apiKey, 'API key')}>
                    <Icon name="copy" size={14} className="mr-1.5" /> Copy
                  </Btn>
                </div>
              </div>
              <div className="flex flex-col gap-[6px]">
                <span className="bm-label-l text-bm-ink">Quick test</span>
                <div className="flex items-start gap-2">
                  <code className={`${MONO_BOX} text-bm-muted`}>{curlExample}</code>
                  <Btn
                    variant="ghost"
                    size="sm"
                    className="w-8 !px-0"
                    onClick={() => copyText(curlExample, 'curl command')}
                    aria-label="Copy curl command"
                  >
                    <Icon name="copy" size={15} />
                  </Btn>
                </div>
                <span className="bm-body-s text-bm-muted">
                  Full endpoint reference is on the API documentation tab.
                </span>
              </div>
            </div>
          ) : (
            <form id="api-key-form" onSubmit={editKey ? handleUpdate : handleCreate} className="flex flex-col gap-5">
              <Field label="Key name">
                <Input
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="e.g. Shoot 360 production"
                  required
                />
              </Field>

              {editKey && (
                <div className="flex items-center justify-between gap-3 rounded-[8px] border border-bm-border bg-bm-subtle px-4 py-3">
                  <div className="flex flex-col">
                    <span className="bm-label-l text-bm-ink">Key status</span>
                    <span className="bm-body-s text-bm-muted">Disabling pauses access and can be undone.</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge tone={form.active ? 'outline' : 'passive'} dot={form.active ? POSITIVE_DOT : undefined}>
                      {form.active ? 'Active' : 'Disabled'}
                    </Badge>
                    <Toggle
                      on={form.active}
                      onChange={(v) => setForm({ ...form, active: v })}
                      label="Key active"
                    />
                  </div>
                </div>
              )}

              <Field label="Partner contact email">
                <Input
                  name="contactEmail"
                  value={form.contactEmail}
                  onChange={handleChange}
                  type="email"
                  placeholder="Optional"
                />
              </Field>

              <Field label="Rate limit (requests per minute)" hint="Use 0 for unlimited.">
                <Input
                  name="rateLimitPerMin"
                  value={form.rateLimitPerMin}
                  onChange={handleChange}
                  type="number"
                  min="0"
                />
              </Field>

              <Field
                label="Allowed browser origins"
                hint="Only needed if the partner calls the API directly from their web pages. Leave empty for server-to-server use."
              >
                <TextArea
                  name="allowedOrigins"
                  value={form.allowedOrigins}
                  onChange={handleChange}
                  rows={2}
                  placeholder={'Optional, comma or newline separated.\ne.g. https://app.shoot360.com'}
                />
              </Field>

              <Field label="Notes">
                <TextArea
                  name="notes"
                  value={form.notes}
                  onChange={handleChange}
                  rows={2}
                  placeholder="Optional internal notes"
                />
              </Field>

              {modalError && <ErrorState>{modalError}</ErrorState>}
            </form>
          )}
        </BmModal>
      )}

      {planKey && (
        <PlanCreditsModal
          apiKey={planKey}
          plans={plans}
          authHeaders={authHeaders}
          onClose={() => setPlanKey(null)}
          onChanged={(updated) => setKeys((ks) => ks.map((k) => (k.id === updated.id ? updated : k)))}
        />
      )}

      {revokeTarget && (
        <ConfirmModal
          title="Revoke API key"
          body={`Revoke "${revokeTarget.name}"? The partner loses access immediately and this cannot be undone.${
            // Revoking never touches Stripe: a paying key's subscription
            // keeps charging until it is cancelled there.
            revokeTarget.billing === 'stripe'
              ? ' This key pays for a plan through Stripe. Cancel that subscription in Stripe too, or the partner keeps being charged.'
              : ''}`}
          confirmLabel="Revoke key"
          tone="danger"
          busy={revoking}
          onConfirm={handleRevoke}
          onClose={() => !revoking && setRevokeTarget(null)}
        />
      )}
    </div>
  );
};

/* --------------------------------- page ---------------------------------- */

const TABS = [
  { value: 'keys', label: 'API keys' },
  { value: 'docs', label: 'API documentation' },
];

const ApiKeysAdminPage = () => {
  const [activeTab, setActiveTab] = useState('keys');
  // The plan catalog from the key list (api_plans.PARTNER_PLAN_DOCS), for
  // the Plan and credits modal and the docs tab's plans section.
  const [plans, setPlans] = useState(null);

  return (
    <div className="w-full min-w-0 max-w-full">
      <PageHeader
        eyebrow="Admin Mode"
        title="API keys"
        subtitle="Issue partner API keys, set each key's plan and credits, and share the documentation partners need to pull Cache athlete data into their platforms."
        actions={(
          <>
            {/* The same docs, on a public route partners can read without a
                login, so hand this link out with new keys. */}
            <Btn href="/p/api-docs" target="_blank">
              <Icon name="external-link" size={15} className="mr-1.5" /> View public docs
            </Btn>
            <Btn variant="secondary" onClick={() => copyText(`${PROD_BASE}/p/api-docs`, 'Public docs link')} aria-label="Copy public docs link">
              <Icon name="copy" size={14} className="mr-1.5" /> Copy link
            </Btn>
          </>
        )}
      />

      <div className="mb-6">
        <SegmentedControl value={activeTab} onChange={setActiveTab} options={TABS} capitalize={false} />
      </div>

      {activeTab === 'keys' && <ApiKeysTab plans={plans} onPlans={setPlans} />}
      {activeTab === 'docs' && <PartnerApiDocs plans={plans} />}
    </div>
  );
};

export default ApiKeysAdminPage;
